import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Eyebrow } from "@/components/site/Sections";
import { StepService, StepProperty, StepCustomer } from "@/components/inspections/BookingSteps";
import { AppointmentPicker } from "@/components/inspections/AppointmentPicker";
import { StepReview, StepSubmit, BookingDone } from "@/components/inspections/ReviewSteps";
import { PriceBreakdown } from "@/components/inspections/PriceBreakdown";
import { useQuote } from "@/components/inspections/useQuote";
import { getInspectionServices, getInspectionOptions, createInspectionBooking, apiError } from "@/lib/api";

const STEPS = ["Select Service", "Property Information", "Customer Information", "Select Appointment", "Review Price", "Submit Booking"];

const initial = (p) => ({
  service_id: p.get("service") || "", address: "", city: "", state: "PA", zip: p.get("zip") || "",
  property_type: p.get("property_type") || "", sqft: p.get("sqft") || "", year_built: "", units: p.get("units") || 1,
  occupancy: "", access_instructions: "", additional_services: [], special_conditions: [],
  full_name: "", email: "", phone: "", customer_type: "", notes: "", date: "", time: "", website: "",
});

function stepError(step, f, quote) {
  if (step === 0 && !f.service_id) return "Please select an inspection service.";
  if (step === 1) {
    if (!f.address || !f.city || !f.state || !/^\d{5}$/.test(f.zip)) return "Please complete the address with a 5-digit ZIP code.";
    if (!f.property_type || !f.sqft || !f.units || !f.occupancy) return "Please complete property type, square footage, units and occupancy.";
    if (quote?.in_service_area === false) return "This ZIP code is outside our current service area. Please call (215) 445-4257.";
  }
  if (step === 2) {
    if (!f.full_name || !f.phone || !f.customer_type) return "Please complete your name, phone and customer type.";
    if (!/^\S+@\S+\.\S+$/.test(f.email)) return "Please enter a valid email.";
  }
  if (step === 3 && (!f.date || !f.time)) return "Please select a date and time.";
  return null;
}

export default function InspectionBook() {
  const [params] = useSearchParams();
  const [f, setF] = useState(() => initial(params));
  const [services, setServices] = useState([]);
  const [options, setOptions] = useState(null);
  const [step, setStep] = useState(params.get("service") ? 1 : 0);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const set = (e) => setF((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  useEffect(() => {
    getInspectionServices().then(setServices).catch(() => setServices([]));
    getInspectionOptions().then(setOptions).catch(() => setOptions(null));
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const svc = useMemo(() => services.find((s) => s.id === f.service_id), [services, f.service_id]);
  const quote = useQuote({ service_id: f.service_id, sqft: f.sqft, property_type: f.property_type, units: f.units, zip: f.zip,
    additional_services: f.additional_services, special_conditions: f.special_conditions });

  const next = () => {
    const err = stepError(step, f, quote);
    if (err) return toast.error(err);
    setStep(step + 1);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await createInspectionBooking({ ...f, sqft: Number(f.sqft), units: Number(f.units) });
      setBooking(res);
    } catch (e) {
      toast.error(apiError(e));
      if (e?.response?.status === 409) setStep(3);
    } finally {
      setSubmitting(false);
    }
  };

  if (booking) return <section className="bg-[#F5F3EC] px-4 py-20"><BookingDone booking={booking} /></section>;

  return (
    <section data-testid="inspection-booking-page" className="min-h-[70vh] bg-[#F5F3EC] py-12 lg:py-16">
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <Eyebrow>Schedule a Property Inspection</Eyebrow>
        <h1 className="mt-3 font-display text-3xl font-bold text-[#0B132B] sm:text-4xl">Book Your Inspection</h1>
        <ol className="mt-8 flex gap-2 overflow-x-auto pb-2" data-testid="booking-progress">
          {STEPS.map((s, i) => (
            <li key={s} className={`flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ${i === step ? "bg-[#0B132B] text-white" : i < step ? "bg-gold/20 text-[#0B132B]" : "bg-white text-slate-400"}`}>
              <span className="font-mono-x">{i + 1}</span> {s}
            </li>
          ))}
        </ol>
        <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_320px]">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10">
            <h2 data-testid="booking-step-title" className="mb-6 font-display text-xl font-bold text-[#0B132B]">{STEPS[step]}</h2>
            {step === 0 && <StepService services={services} value={f.service_id} onSelect={(id) => setF({ ...f, service_id: id, date: "", time: "" })} />}
            {step === 1 && <StepProperty f={f} set={set} setF={setF} options={options} />}
            {step === 2 && <StepCustomer f={f} set={set} />}
            {step === 3 && <AppointmentPicker serviceId={f.service_id} date={f.date} time={f.time} onChange={(v) => setF({ ...f, ...v })} />}
            {step === 4 && <StepReview f={f} svc={svc} quote={quote} />}
            {step === 5 && <StepSubmit f={f} set={set} svc={svc} quote={quote} submitting={submitting} onSubmit={submit} />}
            <div className="mt-10 flex justify-between">
              <button type="button" onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="booking-back-btn"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 disabled:invisible">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              {step < 5 && (
                <button type="button" onClick={next} data-testid="booking-next-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#0B132B] px-6 py-2.5 text-sm font-semibold text-white hover:bg-gold hover:text-[#0B132B]">
                  Continue <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <aside className="space-y-4 lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="font-mono-x text-[10px] uppercase tracking-widest text-gold">Your inspection</p>
              <p data-testid="sidebar-service" className="mt-2 font-semibold text-[#0B132B]">{svc?.name || "No service selected"}</p>
            </div>
            {quote && <PriceBreakdown quote={quote} testid="sidebar-breakdown" />}
          </aside>
        </div>
      </div>
    </section>
  );
}
